import React, { useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Plot {
  id: string;
  plotNumber: string;
  section: string;
  row: string;
  position: number;
  plotSize: 'ADULT' | 'CHILD';
  status: string;
  isAvailable: boolean;
  dimensions?: any;
}

interface PlotGridVisualizerProps {
  rows: Array<{
    name: string;
    plots: Plot[];
  }>;
  selectedPlotId?: string | null;
  onPlotSelect?: (plot: Plot | null) => void;
  readOnly?: boolean;
  showRowLabels?: boolean;
  showPlotNumbers?: boolean;
  gridSize?: 'small' | 'medium' | 'large';
}

const PlotGridVisualizer: React.FC<PlotGridVisualizerProps> = ({
  rows,
  selectedPlotId,
  onPlotSelect,
  readOnly = false,
  showRowLabels = true,
  showPlotNumbers = true,
  gridSize = 'medium'
}) => {
  const [hoveredPlot, setHoveredPlot] = useState<Plot | null>(null);

  const getPlotSizeClasses = (gridSize: string) => {
    switch (gridSize) {
      case 'small':
        return 'w-8 h-8 text-xs';
      case 'large':
        return 'w-16 h-16 text-sm';
      default:
        return 'w-12 h-12 text-xs';
    }
  };

  const getPlotStatusClasses = (plot: Plot) => {
    if (plot.id === selectedPlotId) {
      return 'bg-blue-500 text-white border-blue-600 ring-2 ring-blue-300';
    }

    if (!plot.isAvailable || plot.status !== 'AVAILABLE') {
      return 'bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed';
    }

    return 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200 cursor-pointer';
  };

  const getPlotIcon = (plot: Plot) => {
    if (plot.id === selectedPlotId) {
      return <CheckIcon className={`w-4 h-4 ${getPlotSizeClasses(gridSize).includes('large') ? 'w-6 h-6' : 'w-4 h-4'}`} />;
    }

    if (!plot.isAvailable || plot.status !== 'AVAILABLE') {
      return <XMarkIcon className={`w-4 h-4 ${getPlotSizeClasses(gridSize).includes('large') ? 'w-6 h-6' : 'w-4 h-4'}`} />;
    }

    return null;
  };

  const handlePlotClick = (plot: Plot) => {
    if (readOnly || !plot.isAvailable || plot.status !== 'AVAILABLE') {
      return;
    }

    if (onPlotSelect) {
      // If clicking the already selected plot, deselect it
      if (selectedPlotId === plot.id) {
        onPlotSelect(null);
      } else {
        onPlotSelect(plot);
      }
    }
  };

  const getMaxPlotsInRow = () => {
    return Math.max(...rows.map(row => row.plots.length));
  };

  const formatDimensions = (plot: Plot) => {
    if (plot.dimensions && typeof plot.dimensions === 'object') {
      return `${plot.dimensions.length}m × ${plot.dimensions.width}m`;
    }
    return 'Standard';
  };

  return (
    <div className="plot-grid-visualizer">
      {/* Grid Header */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-200 border border-gray-300 rounded"></div>
          <span>Occupied</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 border border-blue-600 rounded"></div>
          <span>Selected</span>
        </div>
      </div>

      {/* Plot Grid */}
      <div className="overflow-x-auto" style={{ overflow: 'visible' }}>
        <div className="inline-block min-w-full" style={{ overflow: 'visible' }}>
          {rows.map((row, rowIndex) => (
            <div key={row.name} className="flex items-center gap-2 mb-2">
              {showRowLabels && (
                <div className="flex-shrink-0 w-16 text-sm font-medium text-gray-700">
                  Row {row.name}
                </div>
              )}

              <div className="flex gap-1">
                {/* Fill empty slots for proper grid alignment */}
                {Array.from({ length: getMaxPlotsInRow() - row.plots.length }).map((_, emptyIndex) => (
                  <div
                    key={`empty-${rowIndex}-${emptyIndex}`}
                    className={`border border-dashed border-gray-200 rounded ${getPlotSizeClasses(gridSize)}`}
                  />
                ))}

                {row.plots.map((plot) => (
                  <div
                    key={plot.id}
                    className={`
                      relative border-2 rounded-md flex items-center justify-center font-medium
                      transition-all duration-200 hover:scale-105
                      ${getPlotSizeClasses(gridSize)}
                      ${getPlotStatusClasses(plot)}
                    `}
                    onClick={() => handlePlotClick(plot)}
                    onMouseEnter={() => setHoveredPlot(plot)}
                    onMouseLeave={() => setHoveredPlot(null)}
                  >
                    {getPlotIcon(plot)}
                    {showPlotNumbers && !getPlotIcon(plot) && (
                      <span className="truncate">{plot.plotNumber}</span>
                    )}

                    {/* Hover Tooltip */}
                    {hoveredPlot?.id === plot.id && (
                      <div className="absolute z-[9999] mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-nowrap"
                           style={{
                             top: '100%',
                             left: '50%',
                             transform: 'translateX(-50%)',
                             minWidth: '150px'
                           }}>
                        <div className="font-medium">Plot {plot.plotNumber}</div>
                        <div>Section: {plot.section}</div>
                        <div>Row: {plot.row}</div>
                        <div>Size: {plot.plotSize}</div>
                        <div>Dimensions: {formatDimensions(plot)}</div>
                        <div>Status: {plot.isAvailable ? 'Available' : 'Occupied'}</div>
                        <div className="absolute w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"
                             style={{
                               top: '-4px',
                               left: '50%',
                               transform: 'translateX(-50%)'
                             }}></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Total Plots</div>
            <div className="font-medium">
              {rows.reduce((total, row) => total + row.plots.length, 0)}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Available</div>
            <div className="font-medium text-green-600">
              {rows.reduce((total, row) =>
                total + row.plots.filter(plot => plot.isAvailable && plot.status === 'AVAILABLE').length, 0
              )}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Occupied</div>
            <div className="font-medium text-gray-600">
              {rows.reduce((total, row) =>
                total + row.plots.filter(plot => !plot.isAvailable || plot.status !== 'AVAILABLE').length, 0
              )}
            </div>
          </div>
          {selectedPlotId && (
            <div>
              <div className="text-gray-500">Selected</div>
              <div className="font-medium text-blue-600">1</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlotGridVisualizer;